// @ts-check
import React, { useState, useCallback } from 'react';
import { reduxForm, change } from 'utils/reduxFormWrapper';
import HForm, { PlainSelectInput } from 'shared/containers/portal_layout/HForm';
import { ConditionalGroup, ViewConditionalGroup } from '../ConditionalGroup';
import Errors from 'shared/components/Errors';
/* eslint-disable no-unused-vars */
import {
  RuleFormValues,
  Rule as RuleType,
  RulableField,
  RuleThenAction,
} from '../types';
/* eslint-disable no-unused-vars */
import styles from './Rule.module.scss';
import { Button } from 'preferral-ui/components/Button';
import { ExportRuleJSONDrawer } from './ExportRuleJSONDrawer';
import { ImportRuleJSONDrawer } from './ImportRuleJSONDrawer';

/** @typedef {{
  rule: RuleType,
  saveRule: (formValues: RuleFormValues) => Promise<any>,
  deleteRule: () => any,
  availableActions: RuleThenAction[],
  rulableFields: RulableField[]
}} RulePropsType */

/** @type { React.FC<RulePropsType> } */
const Rule = (props) => {
  const {
    rule,
    rulableFields,
    availableActions,
    deleteRule,
    saveRule: doSaveRule,
  } = props;

  const [editing, setEditing] = useState(!rule.persisted);
  const [activeModal, setActiveModal] = useState(null);
  const closeModal = useCallback(() => setActiveModal(null), []);

  const edit = useCallback(() => setEditing(true), []);
  const cancelEdit = useCallback(() => setEditing(false), []);

  const saveRule = useCallback(
    (formValues) => {
      return doSaveRule(formValues).then(cancelEdit);
    },
    [doSaveRule, cancelEdit]
  );

  if (editing) {
    return (
      <FormifiedEditRule
        deleteRule={deleteRule}
        onSubmit={saveRule}
        cancelEdit={cancelEdit}
        persisted={rule.persisted}
        form={`edit-rule-${rule.id}`}
        initialValues={{
          thenAction: rule.thenAction,
          rootConditionalGroupAttributes: rule.rootConditionalGroupAttributes,
        }}
        availableActions={availableActions}
        rulableFields={rulableFields}
      />
    );
  } else {
    const availableAction = availableActions.find(
      (action) => action.value === rule.thenAction
    );
    return (
      <>
        <ExportRuleJSONDrawer
          isOpen={activeModal === 'EXPORT'}
          onClose={closeModal}
          rule={rule}
        />
        <div>
          <div className="pull-right flex items-center gap-1 py-1">
            <Button type="button" size="xs" onClick={edit}>
              Edit Rule
            </Button>
            <Button
              type="button"
              size="xs"
              color="teal"
              onClick={() => setActiveModal('EXPORT')}
            >
              Export Rule
            </Button>
          </div>
          <span>
            IF{' '}
            <ViewConditionalGroup
              attributes={rule.rootConditionalGroupAttributes}
              {...props}
            />
            <br />
            THEN{' '}
            <span className={styles.thenAction}>
              {!!availableAction ? availableAction.name : '<choose action>'}
            </span>
          </span>
        </div>
      </>
    );
  }
};

export default Rule;

const EditRule = (props) => {
  const { dispatch, form: formKey, persisted } = props;
  const [activeModal, setActiveModal] = useState(null);
  const closeModal = useCallback(() => setActiveModal(null), []);

  const onImport = useCallback(
    (rule) => {
      // NB: Mutates the object!
      removePropertyRecursively(rule, 'id');
      dispatch(change(formKey, 'thenAction', rule.thenAction));
      dispatch(
        change(
          formKey,
          'rootConditionalGroupAttributes',
          rule.rootConditionalGroupAttributes
        )
      );
    },
    [dispatch, formKey]
  );
  // dispatch(change('createAgent', 'locationIds', locationIds))

  return (
    <>
      <ImportRuleJSONDrawer
        isOpen={activeModal === 'IMPORT'}
        onClose={closeModal}
        onImport={onImport}
      />
      <HForm onSubmit={props.handleSubmit}>
        {props.error && (
          <div className="row-fluid">
            <div className="span12">
              <Errors errors={props.error || []} />
            </div>
          </div>
        )}
        <div className="row-fluid">
          <div className="pull-left">
            <span className="bold-rule-text">For each new referral:</span>
          </div>

          <div className="pull-right">
            <div className="flex items-center gap-1 py-1">
              <Button
                type="submit"
                size="xs"
                color="blue"
                isLoading={props.submitting}
              >
                Save Rule
              </Button>
              <Button
                type="button"
                size="xs"
                color="red"
                onClick={props.deleteRule}
              >
                Delete Rule
              </Button>
              {!persisted ? (
                <Button
                  type="button"
                  size="xs"
                  color="teal"
                  onClick={() => setActiveModal('IMPORT')}
                >
                  Import Rule
                </Button>
              ) : null}
              <Button type="button" size="xs" onClick={props.cancelEdit}>
                Cancel Edit
              </Button>
            </div>
          </div>
        </div>

        {/* <pre className="text-xs">{JSON.stringify(props, null, 2)}</pre> */}

        <div className="rule-editor-if-then-section">
          <div>
            <div className="rule-control-label if-control-label">
              <span className="bold-rule-text">IF:</span>
            </div>
            <div className="rule-controls if-controls">
              <ConditionalGroup
                name="rootConditionalGroupAttributes"
                rulableFields={props.rulableFields}
              />
            </div>
          </div>

          <div>
            <div className="rule-control-label then-control-label">
              <span className="bold-rule-text">THEN:</span>
            </div>
            <div className="rule-controls then-controls">
              <PlainSelectInput
                prompt="Choose action..."
                name="thenAction"
                className="rule-action"
                options={props.availableActions}
                labelKey="name"
                valueKey="value"
              />
            </div>
          </div>
        </div>
      </HForm>
    </>
  );
};

const FormifiedEditRule = reduxForm()(EditRule);

function removePropertyRecursively(obj, property) {
  for (let prop in obj) {
    if (prop === property) delete obj[prop];
    else if (typeof obj[prop] === 'object')
      removePropertyRecursively(obj[prop], property);
  }
}
